import { apiUrl } from "@/services/http/api-url";

let csrfCache: { token: string; fetchedAt: number } | null = null;
let inFlight: Promise<string> | null = null;
const CSRF_CACHE_MS = 60_000;

/**
 * Shared by httpClient and apiFetch. Single-flight: concurrent callers get the same token, because
 * every fetch rotates the CSRF cookie and would invalidate a token another request is still using.
 */
export async function fetchCsrfToken(force = false): Promise<string> {
  if (!force && csrfCache && Date.now() - csrfCache.fetchedAt < CSRF_CACHE_MS) {
    return csrfCache.token;
  }
  if (inFlight) return inFlight;
  inFlight = requestCsrfToken().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function requestCsrfToken(): Promise<string> {
  const res = await fetch(apiUrl("/v1/csrf"), { credentials: "include" });
  const json = (await res.json()) as { data?: { token?: string } };
  const token = json.data?.token;
  if (!token) {
    throw new Error("CSRF token unavailable");
  }

  csrfCache = { token, fetchedAt: Date.now() };
  return token;
}

export function invalidateCsrfCache(): void {
  csrfCache = null;
}

/**
 * LRN-10: the token already in hand, without a request — for a keepalive send while the page is
 * unloading (no time to fetch one). The token stays valid as long as the cookie is unchanged.
 */
export function peekCsrfToken(): string | null {
  return csrfCache?.token ?? null;
}
